import { Heart, MessageCircle, Send, Bookmark, Share2, Play } from "lucide-react";
import { useState } from "react";

interface ActionBarProps {
  likes: number;
  comments: number;
  liked: boolean;
  onLike: () => void;
  onComment: () => void;
  onShare: () => void;
  likedBy?: string; // "username and 1,234 others"
}

export function ActionBar({
  likes,
  comments,
  liked,
  onLike,
  onComment,
  onShare,
  likedBy,
}: ActionBarProps) {
  const [saved, setSaved] = useState(false);

  const formatCount = (n: number) => {
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
    if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
    return n.toLocaleString();
  };

  return (
    <div className="flex flex-col gap-1.5 py-2.5">
      {/* Action icons row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onLike}
            className="group relative transition-transform active:scale-90"
            aria-label="Like"
          >
            <Heart
              className={`w-[26px] h-[26px] transition-all duration-200 ${
                liked
                  ? "fill-red-500 stroke-red-500 scale-110"
                  : "stroke-foreground group-hover:stroke-red-500"
              }`}
              strokeWidth={liked ? 0 : 1.5}
            />
          </button>

          <button
            onClick={onComment}
            className="group transition-transform active:scale-90"
            aria-label="Comment"
          >
            <MessageCircle
              className="w-[26px] h-[26px] stroke-foreground group-hover:stroke-primary transition-colors"
              strokeWidth={1.5}
            />
          </button>

          <button
            onClick={onShare}
            className="group transition-transform active:scale-90"
            aria-label="Send"
          >
            <Send
              className="w-[22px] h-[22px] stroke-foreground group-hover:stroke-primary transition-colors -rotate-12 translate-y-0.5"
              strokeWidth={1.5}
            />
          </button>

          <button
            onClick={onShare}
            className="group transition-transform active:scale-90"
            aria-label="Share"
          >
            <Share2
              className="w-[24px] h-[24px] stroke-foreground group-hover:stroke-primary transition-colors"
              strokeWidth={1.5}
            />
          </button>
        </div>

        <button
          onClick={() => setSaved((s) => !s)}
          className="transition-transform active:scale-90"
          aria-label="Save"
        >
          <Bookmark
            className={`w-[26px] h-[26px] transition-all duration-200 ${
              saved ? "fill-primary stroke-foreground" : "stroke-foreground"
            }`}
            strokeWidth={saved ? 0 : 1.5}
          />
        </button>
      </div>

      {/* Like count */}
      {likes > 0 && (
        <p className="text-[13px] font-semibold text-foreground leading-none">
          {likedBy || `${formatCount(likes)} likes`}
        </p>
      )}

      {/* Comment count teaser */}
      {comments > 0 && (
        <p className="text-[13px] text-muted-foreground leading-none">
          View all {formatCount(comments)} comments
        </p>
      )}
    </div>
  );
}

interface VideoPlayOverlayProps {
  visible: boolean;
}

export function VideoPlayOverlay({ visible }: VideoPlayOverlayProps) {
  if (!visible) return null;
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <div className="w-14 h-14 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center border border-white/10">
        <Play className="w-7 h-7 text-white fill-white ml-0.5" />
      </div>
    </div>
  );
}
